import { ComboBox, Dialog, TextInput, useToast } from '@ordr-as/umami-react'
import { useState, useMemo, useEffect } from 'react'
import {
    CardScheme,
    LocationSearchDto,
    PaymentFeeDto,
    PaymentFeeDtoCurrencyEnum,
    PaymentProvider,
} from '../../generated-api'
import { useCreatePaymentFee, usePaymentFees } from 'hooks/usePaymentFeeApi'
import { useAllLocations, usePaymentMethods } from 'hooks/usePaymentMethodApi'
import PinCodeDialog from './PinCodeDialog'

type CreatePaymentFeeDialogProps = {
    dialogIsOpen: boolean
    onClose: () => void
    prefillFee?: PaymentFeeDto | null
}

type CardSchemeOverride = {
    cardScheme: CardScheme
    fixedFeeAmount: string
    variableFeePercentage: string
}

const CreatePaymentFeeDialog = (props: CreatePaymentFeeDialogProps) => {
    const { dialogIsOpen, onClose, prefillFee } = props

    const [paymentProvider, setPaymentProvider] = useState<PaymentProvider>(PaymentProvider.UNINTEGRATED)
    const [paymentMethod, setPaymentMethod] = useState('')
    const [currency, setCurrency] = useState<PaymentFeeDtoCurrencyEnum>(PaymentFeeDtoCurrencyEnum.NOK)
    const [fixedFeeAmount, setFixedFeeAmount] = useState('0')
    const [variableFeePercentage, setVariableFeePercentage] = useState('0')
    const [selectedLocations, setSelectedLocations] = useState<LocationSearchDto[]>([])
    const [locationSearchText, setLocationSearchText] = useState('')
    const [pendingCsvLocations, setPendingCsvLocations] = useState<LocationSearchDto[]>([])
    const [cardSchemeOverrides, setCardSchemeOverrides] = useState<CardSchemeOverride[]>([])
    const [pinDialogIsOpen, setPinDialogIsOpen] = useState(false)
    const [pendingGlobalFeeCreation, setPendingGlobalFeeCreation] = useState(false)

    const toast = useToast()
    const { mutate: runCreatePaymentFee, isLoading: isCreating } = useCreatePaymentFee()
    const { data: allLocations } = useAllLocations()
    const { data: allPaymentMethods } = usePaymentMethods()
    const { data: allPaymentFees } = usePaymentFees()

    // Get unique payment methods for the selected provider
    const availablePaymentMethods = useMemo(() => {
        if (!allPaymentMethods) return []
        return allPaymentMethods
            .filter((pm) => pm.paymentProvider === paymentProvider)
            .map((pm) => pm.paymentMethod)
            .filter((value, index, self) => self.indexOf(value) === index) // unique values
            .sort()
    }, [allPaymentMethods, paymentProvider])

    const filteredLocations = useMemo(() => {
        if (!allLocations) return []
        const search = locationSearchText.toLowerCase()
        return allLocations.filter(
            (loc) =>
                !selectedLocations.some((s) => s.locationId === loc.locationId) &&
                (loc.locationName.toLowerCase().includes(search) || loc.locationId.toString().includes(search))
        )
    }, [allLocations, locationSearchText, selectedLocations])

    // Find existing global fee for the selected provider/method/currency combo
    const existingGlobalFee = useMemo(() => {
        if (!allPaymentFees || !paymentProvider || !paymentMethod || !currency) return null

        const feesArray = Array.from(allPaymentFees)
        return feesArray.find(
            (fee) =>
                fee.paymentProvider === paymentProvider &&
                fee.paymentMethod === paymentMethod &&
                fee.currency === currency &&
                (fee.locationId === null || fee.locationId === undefined) // Global fee
        )
    }, [allPaymentFees, paymentProvider, paymentMethod, currency])

    // Prefill form from clicked fee (sets everything including provider/method/currency)
    useEffect(() => {
        if (!dialogIsOpen || !prefillFee) return

        // Set provider, method, currency from the clicked fee
        setPaymentProvider(prefillFee.paymentProvider)
        setPaymentMethod(prefillFee.paymentMethod)
        setCurrency(prefillFee.currency)

        setFixedFeeAmount(prefillFee.fixedFeeAmount.toString())
        setVariableFeePercentage(prefillFee.variableFeePercentage.toString())

        // Prefill card scheme overrides if they exist
        if (prefillFee.cardSchemePriceOverrides && prefillFee.cardSchemePriceOverrides.length > 0) {
            const overrides = prefillFee.cardSchemePriceOverrides.map((override) => ({
                cardScheme: override.cardScheme,
                fixedFeeAmount: override.fixedFeeAmount.toString(),
                variableFeePercentage: override.variableFeePercentage.toString(),
            }))
            setCardSchemeOverrides(overrides)
        } else {
            setCardSchemeOverrides([])
        }

        // Auto-select location if prefillFee has one
        if (prefillFee.locationId && allLocations) {
            const loc = allLocations.find((l) => l.locationId === prefillFee.locationId)
            setSelectedLocations(loc ? [loc] : [])
        } else {
            setSelectedLocations([])
        }
    }, [prefillFee, dialogIsOpen, allLocations])

    // Auto-fill fee amounts when user selects provider/method/currency that has an existing global fee
    useEffect(() => {
        if (!dialogIsOpen || !existingGlobalFee || prefillFee) return

        // Only update fee amounts, not provider/method/currency (user is actively selecting those)
        setFixedFeeAmount(existingGlobalFee.fixedFeeAmount.toString())
        setVariableFeePercentage(existingGlobalFee.variableFeePercentage.toString())

        // Prefill card scheme overrides if they exist
        if (existingGlobalFee.cardSchemePriceOverrides && existingGlobalFee.cardSchemePriceOverrides.length > 0) {
            const overrides = existingGlobalFee.cardSchemePriceOverrides.map((override) => ({
                cardScheme: override.cardScheme,
                fixedFeeAmount: override.fixedFeeAmount.toString(),
                variableFeePercentage: override.variableFeePercentage.toString(),
            }))
            setCardSchemeOverrides(overrides)
        } else {
            setCardSchemeOverrides([])
        }
    }, [existingGlobalFee, dialogIsOpen, prefillFee])

    const handleAddCardSchemeOverride = () => {
        setCardSchemeOverrides([
            ...cardSchemeOverrides,
            { cardScheme: CardScheme.VISA, fixedFeeAmount: '0', variableFeePercentage: '0' },
        ])
    }

    const handleRemoveCardSchemeOverride = (index: number) => {
        setCardSchemeOverrides(cardSchemeOverrides.filter((_, i) => i !== index))
    }

    const handleUpdateCardSchemeOverride = (index: number, field: keyof CardSchemeOverride, value: string) => {
        const updated = [...cardSchemeOverrides]
        updated[index] = { ...updated[index], [field]: value }
        setCardSchemeOverrides(updated)
    }

    const executeCreate = () => {
        // Send as strings for BigDecimalSerializer
        const cardSchemeOverridesForRequest = cardSchemeOverrides.map((override) => ({
            cardScheme: override.cardScheme,
            fixedFeeAmount: override.fixedFeeAmount,
            variableFeePercentage: override.variableFeePercentage,
        }))

        const locationIds: (number | undefined)[] =
            selectedLocations.length > 0 ? selectedLocations.map((loc) => loc.locationId) : [undefined]
        const total = locationIds.length
        let completed = 0

        locationIds.forEach((locId) => {
            runCreatePaymentFee(
                {
                    paymentProvider,
                    paymentMethod: paymentMethod.trim(),
                    currency,
                    // Cast to any to work around TypeScript expecting number but backend needing string
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    fixedFeeAmount: fixedFeeAmount as any,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    variableFeePercentage: variableFeePercentage as any,
                    cardSchemePriceOverrides: (cardSchemeOverridesForRequest.length > 0
                        ? cardSchemeOverridesForRequest
                        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          []) as any,
                    locationId: locId,
                    activeFrom: undefined,
                },
                {
                    onSuccess: () => {
                        completed++
                        if (completed === total) {
                            toast.pushSuccess({
                                id: 'create-payment-fee-success',
                                message:
                                    total > 1
                                        ? `Successfully created ${total} payment fees`
                                        : `Successfully created payment fee`,
                            })
                            handleClose()
                            setPendingGlobalFeeCreation(false)
                        }
                    },
                    onError: (error) => {
                        console.error('Failed to create payment fee:', error)
                        toast.pushError({
                            id: 'create-payment-fee-error',
                            message: 'Failed to create payment fee',
                        })
                        setPendingGlobalFeeCreation(false)
                    },
                }
            )
        })
    }

    const handleCreate = () => {
        if (!paymentMethod.trim()) {
            toast.pushError({
                id: 'create-payment-fee-no-method',
                message: 'Payment method name is required',
            })
            return
        }

        // Validate that the inputs are valid numbers (even though we'll send as strings)
        const fixedFeeNum = parseFloat(fixedFeeAmount)
        const variableFeeNum = parseFloat(variableFeePercentage)

        if (isNaN(fixedFeeNum) || isNaN(variableFeeNum)) {
            toast.pushError({
                id: 'create-payment-fee-invalid-numbers',
                message: 'Fee amounts must be valid numbers',
            })
            return
        }

        // Validate card scheme overrides
        for (const override of cardSchemeOverrides) {
            if (isNaN(parseFloat(override.fixedFeeAmount)) || isNaN(parseFloat(override.variableFeePercentage))) {
                toast.pushError({
                    id: 'create-payment-fee-invalid-override-numbers',
                    message: 'Card scheme override amounts must be valid numbers',
                })
                return
            }
        }

        // Check if this is a global fee (no locations selected)
        const isGlobalFee = selectedLocations.length === 0

        if (isGlobalFee) {
            // Show PIN dialog for global fees
            setPendingGlobalFeeCreation(true)
            setPinDialogIsOpen(true)
            return
        }

        // For location-specific fees, proceed directly
        executeCreate()
    }

    const handlePinVerified = () => {
        setPinDialogIsOpen(false)
        if (pendingGlobalFeeCreation) {
            executeCreate()
        }
    }

    const handlePinCancelled = () => {
        setPinDialogIsOpen(false)
        setPendingGlobalFeeCreation(false)
    }

    const handleClose = () => {
        setPaymentProvider(PaymentProvider.UNINTEGRATED)
        setPaymentMethod('')
        setCurrency(PaymentFeeDtoCurrencyEnum.NOK)
        setFixedFeeAmount('0')
        setVariableFeePercentage('0')
        setSelectedLocations([])
        setLocationSearchText('')
        setPendingCsvLocations([])
        setCardSchemeOverrides([])
        setPinDialogIsOpen(false)
        setPendingGlobalFeeCreation(false)
        onClose()
    }

    return (
        <>
            {dialogIsOpen && (
                <Dialog
                    title="Create Payment Fee"
                    onClose={handleClose}
                    actionLabel={isCreating ? 'Creating...' : 'Create'}
                    onAction={handleCreate}
                    secondaryActionLabel="Cancel"
                    onSecondaryAction={handleClose}
                    isDisabledAction={!paymentMethod.trim() || isCreating}
                    isDisabledSecondaryAction={isCreating}
                >
                    <div className="flex flex-col gap-4">
                        {prefillFee && (
                            <div className="bg-blue-lightest border border-blue-light rounded p-3 text-small">
                                <strong>Note:</strong> Values prefilled from existing{' '}
                                {prefillFee.locationId ? `location-specific (${prefillFee.locationId})` : 'global'} fee
                                for {paymentProvider} - {paymentMethod} ({currency})
                            </div>
                        )}
                        {!prefillFee && existingGlobalFee && (
                            <div className="bg-blue-lightest border border-blue-light rounded p-3 text-small">
                                <strong>Note:</strong> Values prefilled from existing global fee for {paymentProvider} -{' '}
                                {paymentMethod} ({currency})
                            </div>
                        )}

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Payment Provider *
                            </label>
                            <select
                                className="w-full border border-grey-light rounded p-2"
                                value={paymentProvider}
                                onChange={(e) => {
                                    setPaymentProvider(e.target.value as PaymentProvider)
                                    setPaymentMethod('') // Reset payment method when provider changes
                                }}
                            >
                                <option value={PaymentProvider.UNINTEGRATED}>Unintegrated</option>
                                <option value={PaymentProvider.ADYEN}>Adyen</option>
                                <option value={PaymentProvider.SOFTPAY}>SoftPay</option>
                                <option value={PaymentProvider.NETS}>Nets</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Payment Method *
                            </label>
                            {availablePaymentMethods.length > 0 ? (
                                <select
                                    className="w-full border border-grey-light rounded p-2"
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                >
                                    <option value="">-- Select a payment method --</option>
                                    {availablePaymentMethods.map((method) => (
                                        <option key={method} value={method}>
                                            {method}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div className="text-grey-dark p-2 border border-grey-light rounded">
                                    No payment methods found for {paymentProvider}. Create a payment method first.
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">Currency *</label>
                            <select
                                className="w-full border border-grey-light rounded p-2"
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value as PaymentFeeDtoCurrencyEnum)}
                            >
                                {Object.values(PaymentFeeDtoCurrencyEnum).map((curr) => (
                                    <option key={curr} value={curr}>
                                        {curr}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Fixed Fee Amount *
                            </label>
                            <TextInput
                                value={fixedFeeAmount}
                                onChange={(value: string) => setFixedFeeAmount(value)}
                                placeholder="0.00"
                                fullWidth
                            />
                            <p className="text-small text-grey-dark mt-1">Fixed amount in the selected currency</p>
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Variable Fee Percentage *
                            </label>
                            <TextInput
                                value={variableFeePercentage}
                                onChange={(value: string) => setVariableFeePercentage(value)}
                                placeholder="2.5"
                                fullWidth
                            />
                            <p className="text-small text-grey-dark mt-1">Percentage of transaction amount</p>
                        </div>

                        <div
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && pendingCsvLocations.length > 0) {
                                    e.preventDefault()
                                    setSelectedLocations((prev) => [...prev, ...pendingCsvLocations])
                                    setPendingCsvLocations([])
                                    setLocationSearchText('')
                                }
                            }}
                        >
                            <ComboBox<LocationSearchDto>
                                label="Locations (Optional)"
                                placeholder="Search by name or ID..."
                                items={(pendingCsvLocations.length > 0 ? pendingCsvLocations : filteredLocations).map(
                                    (loc) => ({
                                        title: loc.locationName,
                                        subtitle: loc.locationId.toString(),
                                        info: undefined,
                                        additionalData: loc,
                                    })
                                )}
                                onSelectItem={(item) => {
                                    if (item) {
                                        setSelectedLocations((prev) => [...prev, item.additionalData])
                                        setLocationSearchText('')
                                    }
                                }}
                                onInputValueChange={(inputValue) => {
                                    const value = inputValue ?? ''
                                    if (value.includes(',') && allLocations) {
                                        const ids = value
                                            .split(',')
                                            .map((s) => parseInt(s.trim(), 10))
                                            .filter((n) => !isNaN(n))
                                        const matches = allLocations.filter(
                                            (loc) =>
                                                ids.includes(loc.locationId) &&
                                                !selectedLocations.some((s) => s.locationId === loc.locationId)
                                        )
                                        setPendingCsvLocations(matches)
                                        setLocationSearchText(value)
                                        return
                                    }
                                    setPendingCsvLocations([])
                                    setLocationSearchText(value)
                                }}
                            />
                            {pendingCsvLocations.length > 0 && (
                                <div className="mt-2 p-2 bg-blue-lightest border border-blue-light rounded text-small">
                                    Press <strong>Enter</strong> to add {pendingCsvLocations.length} location
                                    {pendingCsvLocations.length > 1 ? 's' : ''}:{' '}
                                    {pendingCsvLocations.map((l) => l.locationName).join(', ')}
                                </div>
                            )}
                            {selectedLocations.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {selectedLocations.map((loc) => (
                                        <span
                                            key={loc.locationId}
                                            className="inline-flex items-center gap-1 bg-purple-lightest border border-purple-light rounded px-2 py-1 text-small"
                                        >
                                            {loc.locationName} ({loc.locationId})
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setSelectedLocations((prev) =>
                                                        prev.filter((l) => l.locationId !== loc.locationId)
                                                    )
                                                }
                                                className="text-grey-dark hover:text-red ml-1"
                                                aria-label={`Remove ${loc.locationName}`}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            <p className="text-small text-grey-dark mt-1">
                                Tip: Paste a comma-separated list of location IDs (e.g. <code>123, 456, 789</code>) and
                                press <strong>Enter</strong> to add multiple at once.
                            </p>
                            <p className="text-small text-grey-dark mt-1">
                                Leave empty for global fee, or select one or more locations for location-specific
                                overrides
                            </p>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-small font-medium text-grey-darkest">
                                    Card Scheme Overrides (Optional)
                                </label>
                                <button
                                    type="button"
                                    onClick={handleAddCardSchemeOverride}
                                    className="text-small text-purple hover:text-purple-dark"
                                >
                                    + Add Override
                                </button>
                            </div>

                            {cardSchemeOverrides.map((override, index) => (
                                <div key={index} className="border border-grey-light rounded p-3 mb-2">
                                    <div className="flex gap-2 mb-3">
                                        <div className="flex-1">
                                            <label className="block text-small font-medium text-grey-darkest mb-1">
                                                Card Scheme
                                            </label>
                                            <select
                                                className="w-full border border-grey-light rounded p-2"
                                                value={override.cardScheme}
                                                onChange={(e) =>
                                                    handleUpdateCardSchemeOverride(
                                                        index,
                                                        'cardScheme',
                                                        e.target.value as CardScheme
                                                    )
                                                }
                                            >
                                                {Object.values(CardScheme).map((scheme) => (
                                                    <option key={scheme} value={scheme}>
                                                        {scheme}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveCardSchemeOverride(index)}
                                                className="text-small text-red hover:text-red-dark px-2 py-2"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="flex-1">
                                            <label className="block text-small font-medium text-grey-darkest mb-1">
                                                Fixed Fee Amount
                                            </label>
                                            <TextInput
                                                value={override.fixedFeeAmount}
                                                onChange={(value: string) =>
                                                    handleUpdateCardSchemeOverride(index, 'fixedFeeAmount', value)
                                                }
                                                placeholder="0.00"
                                                fullWidth
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-small font-medium text-grey-darkest mb-1">
                                                Variable Fee %
                                            </label>
                                            <TextInput
                                                value={override.variableFeePercentage}
                                                onChange={(value: string) =>
                                                    handleUpdateCardSchemeOverride(
                                                        index,
                                                        'variableFeePercentage',
                                                        value
                                                    )
                                                }
                                                placeholder="2.5"
                                                fullWidth
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Dialog>
            )}

            <PinCodeDialog
                isOpen={pinDialogIsOpen}
                onSuccess={handlePinVerified}
                onCancel={handlePinCancelled}
                title="Global Fee Authorization Required"
                message="This action will create or modify a global payment fee that impacts company earnings."
            />
        </>
    )
}

export default CreatePaymentFeeDialog
